import React, { useMemo } from 'react';

const ReadingHeatmap = ({ logs, data, title = "Reading Growth" }) => {
  // heatmapData handles both raw logs (array of log objects) or pre-summed data (array of {date, minutes})
  const heatmapData = useMemo(() => {
    const today = new Date();
    const result = [];
    
    for (let i = 97; i >= 0; i--) {
      const d = new Date();
      d.setDate(today.getDate() - i);
      const dateStr = d.toISOString().split('T')[0];
      
      let dayMinutes = 0;
      if (data) {
        // Use pre-processed data if provided (e.g. from Group/Community query)
        const match = data.find(item => item.date === dateStr);
        dayMinutes = match ? parseInt(match.minutes) : 0;
      } else if (logs) {
        // Fallback to processing raw logs (Personal view)
        dayMinutes = logs
          .filter(log => log.date === dateStr)
          .reduce((sum, log) => sum + (log.minutes || 0), 0);
      }
        
      result.push({ 
        dateStr, 
        minutes: dayMinutes,
        month: d.toLocaleString('default', { month: 'short' })
      });
    }
    return result;
  }, [logs, data]);

  const getLeafColor = (minutes) => {
    if (minutes === 0) return 'var(--border)';
    if (minutes < 15) return 'var(--accent-soft)';
    if (minutes < 60) return '#a5d6a7';
    if (minutes < 180) return 'var(--accent)';
    return '#1b5e20';
  };

  const getLeafScale = (minutes) => {
    if (minutes === 0) return 0.8;
    return 1 + Math.min(minutes / 300, 0.5); // Caps scale growth
  };

  const monthLabels = useMemo(() => {
    const labels = [];
    let lastMonth = '';
    heatmapData.forEach((day, i) => {
      if (day.month !== lastMonth && i % 14 === 0) {
        labels.push({ name: day.month, index: i });
        lastMonth = day.month;
      }
    });
    return labels;
  }, [heatmapData]);

  return (
    <div style={container}>
      <div style={header}>
        <h4 style={titleStyle}>{title}</h4>
      </div>

      <div style={monthLabelRow}>
        {monthLabels.map((m, i) => (
          <span key={i} style={monthLabel}>{m.name}</span>
        ))}
      </div>

      <div style={grid}>
        {heatmapData.map((day, i) => (
          <div 
            key={i} 
            title={`${day.dateStr}: ${day.minutes} mins`}
            style={{
              ...leafStyle,
              backgroundColor: getLeafColor(day.minutes),
              borderRadius: '2px 10px 2px 10px',
              transform: `rotate(${Math.sin(i) * 15}deg) scale(${getLeafScale(day.minutes)})`,
              opacity: day.minutes === 0 ? 0.3 : 1
            }} 
          />
        ))}
      </div>
    </div>
  );
};

const container = {
  marginTop: '15px',
  padding: '15px',
  backgroundColor: 'var(--bg-card)',
  borderRadius: '20px',
  border: '1px solid var(--border)',
};

const header = { display: 'flex', justifyContent: 'space-between', marginBottom: '10px' };
const titleStyle = { margin: 0, fontSize: '0.7rem', color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '1px' };
const monthLabelRow = { display: 'flex', justifyContent: 'space-between', padding: '0 5px', marginBottom: '5px' };
const monthLabel = { fontSize: '0.6rem', color: 'var(--text-soft)' };
const grid = { display: 'grid', gridTemplateColumns: 'repeat(14, 1fr)', gap: '6px' };
const leafStyle = { width: '100%', aspectRatio: '1', transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)' };

export default ReadingHeatmap;
